import { Match, Show, Switch, createSignal, onMount } from "solid-js";
import "./school.css";
import type { AuthModule } from "~/features/auth/auth";
import type { HttpPort } from "~/lib/http";
import { readToken } from "./registration";
import { Shell } from "./SchoolRegistration";
import { createInvitationApi, type InvitationPreview } from "./team";

// Einladungsseite für Lehrkräfte: Link aus der Mail, Name und Passwort festlegen, danach direkt angemeldet.
export function InvitationAccept(props: { http: HttpPort; auth: AuthModule; search: string; onAuthenticated: () => void; onLogin: () => void }) {
  const api = createInvitationApi(props.http);
  const token = readToken(props.search);
  const [preview, setPreview] = createSignal<InvitationPreview | null>(null);
  const [loadError, setLoadError] = createSignal("");
  const [busy, setBusy] = createSignal(false);
  const [error, setError] = createSignal("");

  onMount(async () => {
    try {
      setPreview(await api.preview(token));
    } catch (cause) {
      setLoadError(cause instanceof Error ? cause.message : "Der Link ist ungültig oder abgelaufen.");
    }
  });

  const onSubmit = async (event: SubmitEvent) => {
    event.preventDefault();
    const values = new FormData(event.currentTarget as HTMLFormElement);
    const password = String(values.get("password") ?? "");
    if (password !== String(values.get("repeat") ?? "")) return setError("Die beiden Passwörter stimmen nicht überein.");
    setBusy(true);
    setError("");
    try {
      await props.auth.acceptInvitation({ token, password, firstName: String(values.get("firstName") ?? "").trim(), lastName: String(values.get("lastName") ?? "").trim() });
      props.onAuthenticated();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Die Einladung konnte nicht angenommen werden.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Shell>
      <Switch>
        <Match when={loadError()}>
          <section class="auth-card fade-in">
            <h1 class="sr-title">Einladung nicht gültig</h1>
            <p class="sr-sub">{loadError()} Bitte bitten Sie Ihre Schulleitung, die Einladung noch einmal zu senden.</p>
            <button class="btn btn-full" type="button" onClick={props.onLogin}>Zur Startseite</button>
          </section>
        </Match>
        <Match when={preview()}>
          {(current) => (
            <section class="auth-card fade-in" aria-labelledby="iv-title">
              <h1 class="sr-title" id="iv-title">Willkommen bei Reflexo</h1>
              <p class="sr-sub">{current().inviterName ? `${current().inviterName} von ` : ""}{current().schoolName} hat Sie eingeladen. Legen Sie Ihr Passwort fest, dann geht es direkt los.</p>
              <div class="sr-mailcard">{current().email}</div>
              <form onSubmit={onSubmit}>
                <div class="sr-row2">
                  <div class="form-group"><label class="form-label" for="iv-first">Vorname</label><input id="iv-first" name="firstName" class="form-input" required maxlength={100} value={current().firstName} autocomplete="given-name" /></div>
                  <div class="form-group"><label class="form-label" for="iv-last">Nachname</label><input id="iv-last" name="lastName" class="form-input" required maxlength={100} value={current().lastName} autocomplete="family-name" /></div>
                </div>
                <div class="form-group"><label class="form-label" for="iv-pw">Passwort</label><input id="iv-pw" name="password" class="form-input" type="password" required minlength={10} maxlength={72} autocomplete="new-password" /><p class="sr-hint">Mindestens 10 Zeichen.</p></div>
                <div class="form-group"><label class="form-label" for="iv-pw2">Passwort wiederholen</label><input id="iv-pw2" name="repeat" class="form-input" type="password" required minlength={10} maxlength={72} autocomplete="new-password" /></div>
                <button class="btn btn-pink btn-full btn-lg" type="submit" disabled={busy()}>Konto anlegen und starten →</button>
                <Show when={error()}><div class="form-error" style={{ "margin-top": "12px", "text-align": "center" }} role="alert">{error()}</div></Show>
              </form>
            </section>
          )}
        </Match>
        <Match when={true}><section class="auth-card"><div class="loading-spinner" aria-label="Wird geladen" /></section></Match>
      </Switch>
    </Shell>
  );
}
